import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import TextField from "@mui/material/TextField";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { setFilter, resetFilter } from "../../../../actions/projectmail";
import { initialSearchFilter } from "../../../../reducers/projectmail";
import { useDefaultPasscodeStyles } from "../../../../constants/passCodeUtils";
import { moment } from "../../../../utils/LocalizedUtils";
import TableConfigurator from "../TableLayout/TableConfigurator";

const FilterComponent = forwardRef((props, ref) => {
  const { filter, setFilterFn, resetPage, resetFilterFn, type, openFilter, translate, closeFilter } = props;

  const pc = useDefaultPasscodeStyles();
  const [ownFilter, setOwnFilter] = useState(filter);
  const ownFilterRef = useRef(ownFilter);
  const openFilterRef = useRef(openFilter);
  const typeRef = useRef(type);
  const setFilterFnRef = useRef(setFilterFn);

  useImperativeHandle(ref, () => ({
    setFilter: (filterValue, filterKey) => {
      setOwnFilter({ ...ownFilter, [filterKey]: filterValue });
    },
  }));

  useEffect(() => {
    if (filter === undefined) resetFilterFn(type);
    if (ownFilter === undefined && filter) setOwnFilter(filter);
  }, [filter, openFilter, resetFilterFn, type, ownFilter, setOwnFilter]);

  useEffect(() => {
    ownFilterRef.current = ownFilter;
  }, [ownFilter]);

  useEffect(() => {
    openFilterRef.current = openFilter;
  }, [openFilter]);

  useEffect(() => {
    typeRef.current = type;
  }, [type]);

  useEffect(() => {
    setFilterFnRef.current = setFilterFn;
  }, [setFilterFn]);

  useEffect(() => {
    const enterEvent = e => {
      if (e.key === "Enter" && openFilterRef.current === true) {
        setFilterFnRef.current(ownFilterRef.current, typeRef.current);
      }
    };

    window.addEventListener("keydown", enterEvent);
    return () => {
      window.removeEventListener("keydown", enterEvent);
    };
  }, []);

  const handleTextChange = name => event => {
    setOwnFilter({ ...ownFilter, [name]: event.target.value });
  };

  const handleDateChange = (name, date) => {
    setOwnFilter({ ...ownFilter, [name]: new Date(moment.utc(date)).getTime() });
  };

  const textfield = (label, key) => {
    try {
      return (
        <TextField
          label={translate[TableConfigurator[key].label]}
          sx={[
            pc.textField,
            pc.lineExpander,
            {
              "& input:invalid": {
                background: "#F08080",
              },
            },
          ]}
          value={ownFilter ? ownFilter[key] : ""}
          onChange={handleTextChange(key)}
          slotProps={{
            htmlInput: {
              pattern: TableConfigurator[key].filterProps ? TableConfigurator[key].filterProps : ".*",
            },
          }}
        />
      );
    } catch {
      console.log(key);
      return null;
    }
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", width: "100%" }}>
      {textfield(translate.title, "title")}
      {textfield(translate.customer, "customer")}
      <Box sx={pc.lineBreak} />
      <TextField
        label="Tag"
        sx={[pc.textField, pc.lineExpander]}
        value={ownFilter ? ownFilter.tags : ""}
        onChange={handleTextChange("tags")}
      />
      {textfield(translate.EmailTicketSystemOwner, "owner")}
      {textfield(translate.operator, "operator")}
      <Box sx={pc.lineBreak} />
      {textfield(translate.additionalOperator, "additionalOperator")}
      {textfield(translate.orderProduct, "orderProduct")}
      {textfield(translate.externalId, "externalID")}
      <Box sx={pc.lineBreak} />
      <DateTimePicker
        ampm={false}
        format="DD.MM.YYYY"
        value={
          ownFilter && ownFilter.eventStartTime !== -1 && ownFilter.eventStartTime != null
            ? moment(ownFilter.eventStartTime)
            : null
        }
        onChange={date => handleDateChange("eventStartTime", date)}
        slotProps={{
          textField: {
            label: translate.eventStartTime,
            sx: [pc.textField, pc.lineExpander],
          },
        }}
      />
      <DateTimePicker
        ampm={false}
        format="DD.MM.YYYY"
        value={
          ownFilter && ownFilter.eventEndTime !== -1 && ownFilter.eventEndTime != null
            ? moment(ownFilter.eventEndTime)
            : null
        }
        onChange={date => handleDateChange("eventEndTime", date)}
        slotProps={{
          textField: {
            label: translate.eventEndTime,
            sx: [pc.textField, pc.lineExpander],
          },
        }}
      />
      <Box sx={pc.lineBreak} />
      {textfield(translate.webcastID, "webcastID")}
      {textfield(translate.orderID, "orderID")}
      <Box sx={pc.lineBreak} />
      <Divider />
      <Box sx={{ mt: 1, width: "100%", display: "inline-flex" }}>
        <Box sx={{ flex: 1, width: "10px" }} />
        <Button
          size="small"
          onClick={() => {
            resetFilterFn(type);
            setOwnFilter(initialSearchFilter);
            resetPage();
            closeFilter();
          }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={() => {
            setFilterFn(ownFilter, type);
            resetPage();
          }}
        >
          Suche
        </Button>
      </Box>
    </Box>
  );
});

FilterComponent.defaultProps = {
  filter: undefined,
};

FilterComponent.propTypes = {
  filter: PropTypes.object,
  setFilterFn: PropTypes.func.isRequired,
  resetFilterFn: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  openFilter: PropTypes.bool.isRequired,
  translate: PropTypes.object.isRequired,
  resetPage: PropTypes.func.isRequired,
  closeFilter: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  filter: state.projectmail.searchFilter[ownProps.type],
  translate: state.ui.translate,
});

const mapDispatchToProps = {
  setFilterFn: (filter, type) => dispatch => dispatch(setFilter(filter, type)),
  resetFilterFn: type => dispatch => dispatch(resetFilter(type)),
};

const Filter = connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { forwardRef: true }
)(FilterComponent);

export default Filter;
